/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import {
  Card,
  IconChevronDown,
  IconEllipsis,
  IconEmptyCircle,
  IconInfo,
  IconMinusCircle,
  IconWarningAlt,
  useDirection
} from '@veneer/core';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { generateID } from '../../base/helper';
import Button from '../button';
import './styles.scss';
import { NotificationCardProps } from './types';

const getStrTime = (time: Date | string): string => {
  if (typeof time === 'string') return time;
  const current = new Date();
  const diff = Math.abs(current.getTime() - time.getTime());

  const diffMinutes = Math.floor(diff / 60000);
  if (diffMinutes < 1) return 'Now';
  if (diffMinutes === 1) return '1 minute ago';
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return '1 day ago';

  return `${diffDays} days ago`;
};

const TNotificationCard: React.FC<NotificationCardProps> = ({
  id = generateID('clientos-ui-toolkit-notification-card'),
  title,
  subTitle,
  description,
  time,
  variant,
  read,
  onClick,
  onDeleteClick,
  onReadClick,
  actionLabelMarkRead = 'Mark as read',
  actionLabelDelete = 'Delete',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy
}) => {
  const [isShowAction, setShowAction] = useState(false);
  const [strTime, setStrTime] = useState(() => getStrTime(time));
  const cardRef = useRef<HTMLDivElement>(null);

  const rtl = useDirection().isRTL;

  const notificationIcon = useMemo(() => {
    switch (variant) {
      case 'support':
        return (
          <IconEmptyCircle
            className="icon-support"
            filled={!read}
            aria-hidden="true"
          />
        );
      case 'informative':
        return (
          <IconInfo
            className="icon-informative"
            filled={!read}
            aria-hidden="true"
          />
        );
      case 'warning':
        return (
          <IconWarningAlt
            className="icon-warning"
            filled={!read}
            aria-hidden="true"
          />
        );
      case 'urgent':
        return (
          <IconMinusCircle
            className="icon-urgent"
            filled={!read}
            aria-hidden="true"
          />
        );
      default:
        return (
          <IconEmptyCircle
            className="icon-default"
            aria-hidden="true"
          />
        );
    }
  }, [read, variant]);

  const getEndIcon = useMemo(() => {
    const icon = isShowAction ? (
      <IconChevronDown size={20} />
    ) : (
      <IconEllipsis size={20} />
    );
    const ariaLabel = isShowAction ? 'Chevron down' : 'Ellipsis';

    const handleClick = (event: React.SyntheticEvent) => {
      event.stopPropagation();
      endIconClicked(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        endIconClicked(event);
      }
    };

    return (
      <Button
        type="ghost-flush"
        icon={icon}
        size="medium"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      />
    );
  }, [isShowAction]);

  const isDeleteDisabled = useMemo(() => {
    return variant === 'urgent';
  }, [variant]);

  const endIconClicked = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      if (variant !== 'default') setShowAction((prev) => !prev);
    },
    [variant]
  );

  const readClicked = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      onReadClick && onReadClick();
    },
    [onReadClick]
  );

  const deleteClicked = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      if (!isDeleteDisabled && onDeleteClick) onDeleteClick();
    },
    [isDeleteDisabled, onDeleteClick]
  );

  useEffect(() => {
    if (typeof time === 'string') return;
    const timer = setInterval(() => {
      setStrTime(getStrTime(time));
    }, 60000);
    return () => clearInterval(timer);
  }, [time]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setShowAction(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [cardRef]);

  const getAriaLiveByVariant = (variant?: string) => {
    if (variant === 'urgent' || variant === 'warning') return 'assertive';
    return 'polite';
  };

  return (
    <Card
      ref={cardRef}
      id={id}
      className={`clientos-ui-toolkit-notification-card ${variant} ${read ? 'read status-read' : ''}`}
      aria-label={ariaLabel || `${variant ? variant : 'default'} notification`}
      aria-describedby={ariaDescribedBy || `${id}__notification-message-text`}
      content={
        <div
          className="clientos-ui-toolkit-notification-card__notification-container"
          onClick={() => setShowAction(false)}
          id={`${id}__notification-container`}
          aria-live={getAriaLiveByVariant(variant)}
          aria-atomic="true"
        >
          <div
            className="clientos-ui-toolkit-notification-card__info-icon"
            id={`${id}__info-icon`}
          >
            {notificationIcon}
          </div>
          <div
            className="clientos-ui-toolkit-notification-card__notification-content"
            id={`${id}__notification-content`}
          >
            <div
              className="clientos-ui-toolkit-notification-card__header-container"
              id={`${id}__header-container`}
            >
              <div
                className="clientos-ui-toolkit-notification-card__header"
                id={`${id}__header`}
              >
                <div
                  className="clientos-ui-toolkit-notification-card__title"
                  id={`${id}__title`}
                >
                  <h2
                    className="subtitle-regular--medium"
                    id={`${id}__title-text`}
                  >
                    {title}
                  </h2>
                </div>
                <div
                  className="clientos-ui-toolkit-notification-card__sub-title"
                  id={`${id}__sub-title`}
                >
                  <h3
                    className="caption-regular"
                    id={`${id}__sub-title-text`}
                  >
                    {subTitle}
                  </h3>
                </div>
              </div>
            </div>
            <div
              className="clientos-ui-toolkit-notification-card__description-container"
              id={`${id}__description-container`}
            >
              <div
                className="clientos-ui-toolkit-notification-card__notification-message"
                id={`${id}__notification-message`}
              >
                <p
                  className="caption-large"
                  id={`${id}__notification-message-text`}
                >
                  {description}
                </p>
              </div>
              <div
                className="clientos-ui-toolkit-notification-card__time"
                id={`${id}__time`}
              >
                <p
                  className="caption-regular"
                  id={`${id}__time-text`}
                >
                  {strTime}
                </p>
              </div>
            </div>
          </div>
          <div
            className={`clientos-ui-toolkit-notification-card__dropdown ${rtl ? 'clientos-ui-toolkit-notification-card__dropdown-rtl' : 'clientos-ui-toolkit-notification-card__dropdown-ltr'}`}
            id={`${id}__dropdown`}
          >
            <div
              className="clientos-ui-toolkit-notification-card__dropdown-icon-container"
              id={`${id}__dropdown-icon-container`}
            >
              <div
                className="clientos-ui-toolkit-notification-card__end-icon"
                id={`${id}__end-icon`}
              >
                {getEndIcon}
              </div>
            </div>
            {isShowAction && (
              <Card
                className="clientos-ui-toolkit-notification-card__notification-action-card"
                id={`${id}__notification-action-card`}
                content={
                  <div
                    className="clientos-ui-toolkit-notification-card__action-card"
                    id={`${id}__action-card`}
                  >
                    {!read && (
                      <div
                        role="button"
                        id={`${id}__mark-as-read`}
                        className="clientos-ui-toolkit-notification-card__action-button"
                        onClick={readClicked}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            readClicked(event);
                          }
                        }}
                        tabIndex={0}
                      >
                        <span
                          className="label"
                          id={`${id}__mark-as-read-label`}
                        >
                          {actionLabelMarkRead}
                        </span>
                      </div>
                    )}
                    <div
                      role="button"
                      className={`clientos-ui-toolkit-notification-card__action-button ${isDeleteDisabled ? 'disabled' : ''}`}
                      id={`${id}__delete`}
                      onClick={deleteClicked}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          deleteClicked(event);
                        }
                      }}
                      tabIndex={0}
                    >
                      <span
                        className="label"
                        id={`${id}__delete-label`}
                      >
                        {actionLabelDelete}
                      </span>
                    </div>
                  </div>
                }
              />
            )}
          </div>
        </div>
      }
      onClick={onClick}
    />
  );
};
export const NotificationCardAnalytics = withAnalytics(TNotificationCard);
export default memo(TNotificationCard);
